import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { deckKeys, reviewQueueKey } from '@/entities/deck';
import { learningKeys } from '@/entities/learning';
import { importAnki, importTable } from '@/shared/api/imports';
import type { ImportResult } from '@/shared/api/types';
import { Button, ErrorMessage, TextField } from '@/shared/ui';

import styles from './ImportForms.module.css';

function FileField({
  label,
  hint,
  accept,
  onChange,
}: {
  label: string;
  hint: string;
  accept: string;
  onChange: (file: File | null) => void;
}): React.JSX.Element {
  const id = useId();

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={styles.file}
        type="file"
        accept={accept}
        aria-describedby={`${id}-hint`}
        onChange={(event) => {
          onChange(event.target.files?.[0] ?? null);
        }}
      />
      <span className={styles.hint} id={`${id}-hint`}>
        {hint}
      </span>
    </div>
  );
}

function Outcome({ result }: { result: ImportResult }): React.JSX.Element {
  const { t } = useTranslation();
  const { skipped } = result;

  return (
    <div className={styles.outcome} role="status">
      <p className={styles.done}>{t('import.done', { count: result.decks.length })}</p>
      <ul className={styles.decks}>
        {result.decks.map((deck) => (
          <li key={deck.id}>
            <Link to={`/decks/${deck.id}`}>{deck.title}</Link>{' '}
            <span className={styles.muted}>{t('decks.cards', { count: deck.card_count })}</span>
          </li>
        ))}
      </ul>
      {result.reviews > 0 && <p>{t('import.reviews', { count: result.reviews })}</p>}
      {skipped.empty > 0 && (
        <p className={styles.muted}>{t('import.skippedEmpty', { count: skipped.empty })}</p>
      )}
      {skipped.too_long > 0 && (
        <p className={styles.muted}>{t('import.skippedTooLong', { count: skipped.too_long })}</p>
      )}
      {skipped.media > 0 && (
        <p className={styles.muted}>{t('import.skippedMedia', { count: skipped.media })}</p>
      )}
    </div>
  );
}

function useImport<T>(run: (input: T) => Promise<ImportResult>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: run,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: deckKeys.all }),
        queryClient.invalidateQueries({ queryKey: learningKeys.all }),
        queryClient.invalidateQueries({ queryKey: reviewQueueKey }),
      ]),
  });
}

/**
 * Imports an Anki package: one deck per top-level Anki deck, with the answer
 * history, so that what was learned keeps its progress.
 */
export function ImportAnkiForm(): React.JSX.Element {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const mutation = useImport((input: File) => importAnki(input));

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (file && !mutation.isPending) {
      mutation.mutate(file);
    }
  };

  return (
    <form className={styles.form} onSubmit={submit} aria-label={t('import.ankiTitle')}>
      <FileField
        label={t('import.ankiFile')}
        hint={t('import.ankiHint')}
        accept=".apkg,.colpkg"
        onChange={setFile}
      />
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      <Button
        type="submit"
        variant="primary"
        disabled={!file || mutation.isPending}
        className={styles.submit}
      >
        {mutation.isPending ? t('import.working') : t('import.submit')}
      </Button>
      {mutation.data && <Outcome result={mutation.data} />}
    </form>
  );
}

/** Imports a table: question in the first column, answer in the second. */
export function ImportTableForm(): React.JSX.Element {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [header, setHeader] = useState(false);
  const mutation = useImport((input: File) => importTable(input, { title, header }));
  const headerId = useId();

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (file && !mutation.isPending) {
      mutation.mutate(file);
    }
  };

  return (
    <form className={styles.form} onSubmit={submit} aria-label={t('import.tableTitle')}>
      <FileField
        label={t('import.tableFile')}
        hint={t('import.tableHint')}
        accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
        onChange={setFile}
      />
      <TextField
        label={t('import.deckTitle')}
        hint={t('import.deckTitleHint')}
        maxLength={200}
        value={title}
        onChange={(event) => {
          const { value } = event.target;
          setTitle(value);
        }}
      />
      <div className={styles.check}>
        <input
          id={headerId}
          type="checkbox"
          checked={header}
          onChange={(event) => {
            const { checked } = event.target;
            setHeader(checked);
          }}
        />
        <label htmlFor={headerId}>{t('import.header')}</label>
      </div>
      {mutation.isError && <ErrorMessage error={mutation.error} />}
      <Button
        type="submit"
        variant="primary"
        disabled={!file || mutation.isPending}
        className={styles.submit}
      >
        {mutation.isPending ? t('import.working') : t('import.submit')}
      </Button>
      {mutation.data && <Outcome result={mutation.data} />}
    </form>
  );
}
